import { useQuery } from '@tanstack/react-query';
import { garmentTimelinePath } from '@core/api/queryPaths';

import { apiFetch } from '@/api/client';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { hueClasses } from '@/components/hues';
import { EmptyState, Notice } from '@/components/PageHeading';
import { formatDate, formatDateTime } from '@core/format/dates';
import {
  eventTitle,
  garmentTimelineKey,
  eventWho,
  isSpan,
  sourceHue,
  sourceLabel,
  withheldNotice,
  type GarmentTimeline as Timeline,
  type TimelineEvent,
} from '@core/units/timeline';
import { sourceIcon } from './timelineIcons';

/**
 * Where a garment has been and what it has been through (counter UX redesign §5).
 *
 * <p>
 * <strong>This question took four screens.</strong> The status trail was on the units row, the
 * calendar on availability, the bookings in the register and the jobs in the two queues — and
 * nobody standing at a counter holding a sherwani has four screens' worth of patience.
 * </p>
 *
 * <p>
 * <strong>One panel, four tracks, kept visibly apart.</strong> `UnitStatus`, `GarmentState` and
 * `GarmentJobStatus` are three state machines that overlap in vocabulary, so each entry wears the
 * hue and the phrasing of the record it came from rather than being flattened into one invented
 * track — see `timeline.ts`, which holds all of that and is tested on its own.
 * </p>
 *
 * <p>
 * A modal rather than a page, because there is no router and a detail address needs route
 * parameters. It is opened from a row, and it replaced the old `HistoryDrawer`, which showed one of
 * the four tracks.
 * </p>
 *
 * <p>
 * <strong>It wears the template's search-modal shell</strong> — the same centred 630px card, the
 * same titled header with a framed close control, the same surface tokens — so the two things the
 * shell can put over a screen look like one product rather than two. It was a right-hand drawer
 * first; the drawer form is defensible for a long chronological list, but matching mattered more
 * than the extra height, and the list scrolls inside the card instead.
 * </p>
 */
export function GarmentTimeline({
  unitId,
  serialCode,
  subtitle,
  onClose,
}: {
  unitId: number;
  serialCode: string;
  /** The design and configuration, so the panel names the garment the way its row does. */
  subtitle?: string;
  onClose: () => void;
}) {
  const timeline = useQuery({
    queryKey: [...garmentTimelineKey, unitId],
    queryFn: () => apiFetch<Timeline>(garmentTimelinePath(unitId)),
  });

  const withheld = timeline.data ? withheldNotice(timeline.data) : null;

  return (
    /*
      `z-9999` is the app-overlay rung — see the ladder in `style.css`. `data-shortcut-layer` takes
      the whole shortcut vocabulary while this is open, so Alt+P here does not print the list behind
      it.
    */
    <div
      className="fixed inset-0 z-9999 overflow-y-auto bg-[var(--overlay-scrim)]"
      data-shortcut-layer
      role="dialog"
      aria-modal="true"
      aria-label={`History of ${serialCode}`}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="flex min-h-full items-start justify-center px-4 py-[10vh]">
        <div className="w-full max-w-[630px] rounded-lg border border-[var(--overlay-border)] bg-[var(--overlay-surface)] p-6 shadow-lg">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              {/*
                The serial is the heading, because that is what the person is holding. It stays in
                the tabular face for the reason every code in this product does — a run of near
                identical serials only reads as different when the digits line up.
              */}
              <p className="text-title font-mono-nums truncate text-lg font-bold">{serialCode}</p>
              <p className="text-default mt-0.5 truncate text-[12.5px]">
                {subtitle ? `${subtitle} · history` : 'History'}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="border-border-color text-title hover:bg-danger hover:border-danger inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border bg-white text-base hover:text-white"
            >
              <i className="icon icon-x" />
            </button>
          </div>

          {timeline.isLoading && <p className="text-default text-[13px]">Loading…</p>}

          {timeline.isError && <ApiErrorAlert error={timeline.error} />}

          {withheld && (
            <div className="mb-5">
              {/*
                Said out loud rather than left as a gap. A timeline quietly missing its bookings
                reads as a garment nobody has ever rented, which is the answer a storeman would
                carry to a manager.
              */}
              <Notice tone="info" title="Part of this history is not shown">
                {withheld}
              </Notice>
            </div>
          )}

          {timeline.data?.events.length === 0 && (
            <EmptyState
              icon="icon icon-history"
              title="Nothing recorded yet"
              hint="Every move this garment makes will be listed here."
            />
          )}

          {/*
            The list scrolls inside the card rather than the card growing with it. A garment three
            seasons old carries dozens of entries, and a modal taller than the viewport puts its
            own close button off screen.
          */}
          {(timeline.data?.events.length ?? 0) > 0 && (
            <ol className="flex max-h-[52vh] flex-col gap-3 overflow-y-auto">
              {(timeline.data?.events ?? []).map((entry) => (
                <Entry key={`${entry.source}:${entry.refId}`} entry={entry} />
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Entry({ entry }: { entry: TimelineEvent }) {
  const hue = hueClasses[sourceHue[entry.source]];
  const who = eventWho(entry);

  return (
    <li className="flex gap-3">
      <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-md ${hue.wash}`}
        aria-hidden="true"
      >
        <i className={`${sourceIcon[entry.source]} text-[14px] ${hue.ink}`} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-[13px] font-medium text-gray-900">{eventTitle(entry)}</span>
          {/*
            The track's name beside every entry, not only where they group. Four kinds of record in
            one list is exactly where a reader needs telling which they are looking at.
          */}
          <span className={`text-[11px] font-semibold ${hue.ink}`}>
            {sourceLabel[entry.source]}
          </span>
        </div>

        {entry.reason && <p className="text-[12.5px] text-gray-900">{entry.reason}</p>}

        <span className="text-default font-mono-nums block text-[11px]">
          {/*
            A range for the two sources that cover calendar days, a timestamp for the two that
            happen at a moment. Printing a time of day against a block would invent precision the
            record does not have.
          */}
          {isSpan(entry)
            ? `${formatDate(entry.fromDate)} → ${formatDate(entry.toDate)}`
            : formatDateTime(entry.occurredOn)}
          {who ? ` · ${who}` : ''}
        </span>
      </div>
    </li>
  );
}
